import { memo } from "react";
import Modal from "../Modal/Modal";
import clsx from "clsx";
import { MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";
import CheckBox from "../CheckBox/CheckBox";
import CustomSelect, { ECustomSelectIconType } from "../Select/CustomSelect";
import useTheme from "src/hooks/useTheme";
import useModalsStore from "src/store/modalsStore";
import SettingsPerDeviceInfoBlock from "../InfoBlock/SettingsPerDeviceInfoBlock/SettingsPerDeviceInfoBlock";
import { ETheme } from "src/store/useGlobalPreferencesStore";
import useUiPreferences from "src/store/useUiPreferences";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

const ThemeSettingsModal: React.FC = () => {
  const { t } = useTranslation();
  const miscUiPreferences = useUiPreferences((s) => s.misc);
  const setUiPreferences = useUiPreferences((s) => s.setUiPreferences);

  const { setShowThemeSettingsModal } = useModalsStore();
  const { isDarkMode, setDarkMode } = useTheme();

  return (
    <Modal
      onEscape={() => setShowThemeSettingsModal(false)}
      className="md:!max-w-[480px] md:!min-w-[280px] md:w-full"
    >
      <Modal.Header
        onClose={() => setShowThemeSettingsModal(false)}
        title="Tema y otras opciones"
      />
      <Modal.Body>
        <div
          className={clsx(["flex", "w-full", "flex-col", "gap-4", "md:gap-6"])}
        >
          <SettingsPerDeviceInfoBlock />

          <div
            className={clsx([
              "flex",
              "grid",
              "grid-cols-1",
              "md:grid-cols-1",
              "gap-2",
              "md:gap-4",
            ])}
          >
            <CustomSelect
              label={t("pages:appearance.collapsibles.this_device.theme.title")}
              options={[
                {
                  value: "dark",
                  label: t(
                    "pages:appearance.collapsibles.this_device.theme.dark_mode",
                  ),
                  icon: "MdDarkMode",
                  iconType: ECustomSelectIconType.ICON,
                },
                {
                  value: "light",
                  label: t(
                    "pages:appearance.collapsibles.this_device.theme.light_mode",
                  ),
                  icon: "MdLightMode",
                  iconType: ECustomSelectIconType.ICON,
                },
              ]}
              withIcons
              value={isDarkMode ? ETheme.DARK : ETheme.LIGHT}
              onChange={(e) => setDarkMode(e?.value === ETheme.DARK)}
              required
              isClearable={false}
              useValueAsLabel={false}
              isSearchable={false}
            />
            <CheckBox
              label={t(
                "pages:appearance.collapsibles.this_device.complex_animations.title",
              )}
              onChange={(isChecked: boolean) => {
                setUiPreferences({
                  misc: {
                    enableComplexAnimations: isChecked,
                  },
                });
              }}
              checked={miscUiPreferences.enableComplexAnimations}
              description={t(
                "pages:appearance.collapsibles.this_device.complex_animations.description",
              )}
            />
            <CheckBox
              label={t(
                "pages:appearance.collapsibles.this_device.title_bar.title",
              )}
              onChange={(isChecked: boolean) => {
                setUiPreferences({
                  misc: {
                    enableMobileTitleBar: isChecked,
                  },
                });
              }}
              checked={miscUiPreferences.enableMobileTitleBar}
              description={t(
                "pages:appearance.collapsibles.this_device.title_bar.description",
              )}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            text: t("common:buttons.close"),
            onClick: () => setShowThemeSettingsModal(false),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.SM} />,
          },
        ]}
      />
    </Modal>
  );
};

export default memo(ThemeSettingsModal);
